import { FaTrash } from "react-icons/fa";
import type React from "react";
import { useState, useRef } from "react";

import type { TConditionalFormattingRule, TConditionalFormattingRuleOptions } from "../../../../data";
import { ColorPicker } from "../../../grid-settings/ColorPicker";
import { ManageTable, InputOrReference, TruncatedTooltip } from "../Shared";
import { Button, Checkbox, Dropdown, Toggle } from "../../../ui";
import { Tooltip, TooltipTrigger } from "@artis/ui";
import { useGridSettings, useUpdateGridSettings, type TGridSettings } from "../../../../data";
import { useUserId } from "../../../../context/auth/hooks";

export const ConditionalFormattingRuleRow = ({
  value,
  pageId,
  affected,
  selected,
  onSelect,
  onUpdate,
  onRemove,
  limitWidth,
}: {
  value: TConditionalFormattingRuleOptions;
  pageId: string;
  affected?: React.ReactNode;
  selected?: boolean;
  onSelect?: () => void;
  onUpdate: (rule: Partial<TConditionalFormattingRule>) => void;
  onRemove?: () => void;
  limitWidth?: number;
}) => {
  // Convert color format for display
  const colorValue = value.bg_color === "#ffffff00" || !value.bg_color ? "inherit" : value.bg_color;
  const [showColorPicker, setShowColorPicker] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const settings = useGridSettings(useUserId());
  const update = useUpdateGridSettings();

  const savedColoursRaw = settings.data?.saved_colours;
  const savedColours = Array.isArray(savedColoursRaw)
    ? (savedColoursRaw.filter((c): c is string => typeof c === "string") as string[])
    : ([] as string[]);

  const handleSavedColoursChange = (colours: string[]) => {
    update.optimistic({ saved_colours: colours as unknown as TGridSettings["saved_colours"] });
  };

  return (
    <ManageTable.Row>
      {onSelect && (
        <div className="flex justify-center">
          <Checkbox checked={selected} onChange={onSelect} />
        </div>
      )}
      {onSelect && (
        <div
          className="border-r border-outline h-full"
          style={{ marginRight: "var(--spacing-scale-small-200, 6px)" }}
        />
      )}
      {affected && (
        <TruncatedTooltip title={affected as string}>
          <div
            className="border rounded-md px-3 w-[104px] truncate bg-surface-1 flex items-center box-border overflow-hidden"
            style={{ height: "27px" }}
          >
            <span data-truncate-check className="truncate block w-full">
              {affected}
            </span>
          </div>
        </TruncatedTooltip>
      )}
      <div className="">
        <Dropdown
          value={value.rule}
          onChange={(newValue) => onUpdate({ rule: newValue as TConditionalFormattingRule["rule"] })}
          options={[
            { value: "equals", label: "Equals" },
            { value: "isNotEqual", label: "Is not equal" },
            { value: "lessThan", label: "Less than" },
            { value: "moreThan", label: "More than" },
            { value: "lessThanOrEqual", label: "Less than or equal" },
            { value: "moreThanOrEqual", label: "More than or equal" },
          ]}
          className="w-[100px] h-[27px]"
        />
      </div>
      <InputOrReference
        pageId={pageId}
        value={value.limit}
        onChange={(limit) => onUpdate({ limit })}
        width={limitWidth ?? 176}
      />
      <div className="relative">
        <TooltipTrigger>
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="flex items-center justify-center cursor-pointer"
            style={{
              width: "63px",
              height: "24px",
              backgroundColor: colorValue === "inherit" ? "var(--color-surface-1)" : colorValue,
              border: colorValue === "inherit" ? "1px dashed var(--neutral-default)" : "none",
              borderRadius: "var(--border-radius-rounded-lg, 6px)",
            }}
            data-testid="color-picker"
          >
            {colorValue === "inherit" ? (
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M6 1V11M1 6H11" stroke="var(--neutral-default)" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            ) : (
              <span
                style={{
                  color: value.invert_text_color ? "white" : "black",
                  fontWeight: value.bold_text ? "bold" : "normal",
                  fontSize: "12px",
                }}
              >
                123
              </span>
            )}
          </button>
          <Tooltip>Set formatting</Tooltip>
        </TooltipTrigger>
        <ColorPicker
          value={colorValue}
          onChange={(color) => {
            const newColor = color === "inherit" ? "#ffffff00" : color;
            onUpdate({ bg_color: newColor });
          }}
          position="bottom"
          savedColours={savedColours}
          onSavedColoursChange={handleSavedColoursChange}
          open={showColorPicker}
          onOpenChange={setShowColorPicker}
          anchorRef={buttonRef as React.RefObject<HTMLElement>}
          additionalSettings={
            <div className="flex flex-col gap-[3px] w-full px-3">
              <div className="flex items-center justify-between w-full">
                <p className="text-[var(--toast-foreground,#101010)] text-[var(--font-size-base,12px)] font-normal leading-[18px]">
                  Invert text
                </p>
                <Toggle
                  checked={!!value.invert_text_color}
                  onChange={() => onUpdate({ invert_text_color: !value.invert_text_color })}
                />
              </div>
              <div className="flex items-center justify-between w-full">
                <p className="text-[var(--toast-foreground,#101010)] text-[var(--font-size-base,12px)] font-normal leading-[18px]">
                  Bold text
                </p>
                <Toggle checked={!!value.bold_text} onChange={() => onUpdate({ bold_text: !value.bold_text })} />
              </div>
            </div>
          }
        />
      </div>
      <div className="">
        <input
          type="text"
          value={value.note}
          className="max-w-full px-2 border border-outline rounded-md text-sm bg-surface-1 box-border focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          style={{ height: "27px" }}
          placeholder="Add note..."
          onChange={(e) => onUpdate({ note: e.target.value })}
        />
      </div>
      <div className="flex justify-center items-center">
        <TooltipTrigger>
          <div>
            <Checkbox checked={value.stop_if_true} onChange={() => onUpdate({ stop_if_true: !value.stop_if_true })} />
          </div>
          <Tooltip>Stop evaluating rules if this condition is met</Tooltip>
        </TooltipTrigger>
      </div>
      {onRemove && (
        <div className="pl-2">
          <TooltipTrigger>
            <div>
              <Button variant="outline" color="neutral" size="iconOnly" iconWidth={36} onClick={onRemove}>
                <FaTrash />
              </Button>
            </div>
            <Tooltip>Delete this rule</Tooltip>
          </TooltipTrigger>
        </div>
      )}
    </ManageTable.Row>
  );
};
